// `/admin/reconciliation` (croqui `screens/admin/conciliacao.tsx`). Foco declarado:
// **as divergências abertas**, é o número-herói do topo e a tabela do meio; o kill
// switch, a saúde por tipo e o histórico descem de nível.
//
// Esta é a rota-PAI: ela desenha a tela inteira e, por cima, o `RouteDrawer` com o
// `<Outlet/>` da rota `runs/$id` (`spec/24` §1 decisão 5, drawer `ExecucaoConciliacao`).
// Deep link em `/admin/reconciliation/runs/rec_…` monta a tela embaixo e o drawer aberto.
//
// O `403 mfa_required` do `POST /admin/v1/reconciliation/run` não é tratado aqui:
// `api/client.ts` abre o `StepUpDialog` e reexecuta a chamada (`spec/17` §4.3).

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Outlet, useMatch, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  RECONCILIATION_KEY,
  reconciliationRunsQueryOptions,
  reconciliationStatusQueryOptions,
  type StartRunBody,
  startReconciliationRun,
  unlockPayout,
} from '../api/queries/admin-reconciliation.ts';
import { Btn } from '../components/kit/btn.tsx';
import { Mono } from '../components/kit/mono.tsx';
import { PageHeader } from '../components/kit/page-header.tsx';
import { Panel } from '../components/kit/panel.tsx';
import { Pill } from '../components/kit/pill.tsx';
import { RouteDrawer } from '../components/kit/route-drawer.tsx';
import { payoutsPausedQueryOptions } from '../features/admin-overview/queries.ts';
import { OpenDivergences } from '../features/admin-reconciliation/open-divergences.tsx';
import {
  divergenceKey,
  type OpenDivergence,
  openDivergencesQueryOptions,
} from '../features/admin-reconciliation/queries.ts';
import { useRunDrawerHeader } from '../features/admin-reconciliation/run-detail.tsx';
import { RunDialog } from '../features/admin-reconciliation/run-dialog.tsx';
import { RunsList } from '../features/admin-reconciliation/runs-list.tsx';
import { RunningNotice, StatusPanel } from '../features/admin-reconciliation/status-panel.tsx';
import { UnlockDialog } from '../features/admin-reconciliation/unlock-dialog.tsx';

/** Largura do drawer no croqui (`width={440}`). */
const DRAWER_WIDTH = 440;

function Reconciliation() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [runOpen, setRunOpen] = useState(false);
  const [unlocking, setUnlocking] = useState<OpenDivergence | undefined>(undefined);

  const status = useQuery(reconciliationStatusQueryOptions());
  const paused = useQuery(payoutsPausedQueryOptions());
  const divergences = useInfiniteQuery(openDivergencesQueryOptions());
  const runs = useInfiniteQuery(reconciliationRunsQueryOptions());

  // A rota `runs/$id` é filha desta: se ela casa, a execução está aberta por cima.
  const detail = useMatch({ from: '/admin/reconciliation/runs/$id', shouldThrow: false });
  const header = useRunDrawerHeader(detail?.params.id);

  const refresh = async (): Promise<void> => {
    // Sem evento de conciliação no stream (`spec/17` §4.5): quem atualiza é isto.
    await queryClient.invalidateQueries({ queryKey: [RECONCILIATION_KEY] });
  };

  const start = useMutation({
    mutationFn: (body: StartRunBody) => startReconciliationRun(body),
    onSuccess: async () => {
      setRunOpen(false);
      await refresh();
    },
  });

  const unlock = useMutation({
    mutationFn: (input: { sellerId: string; reason: string }) =>
      unlockPayout(input.sellerId, { reason: input.reason }),
    onSuccess: async () => {
      setUnlocking(undefined);
      await refresh();
    },
  });

  const openRun = (id: string): void => {
    void navigate({ to: '/admin/reconciliation/runs/$id', params: { id } });
  };
  const closeRun = (): void => {
    void navigate({ to: '/admin/reconciliation' });
  };

  const divergenceRows = divergences.data?.pages.flatMap((page) => page.data) ?? [];
  const runRows = runs.data?.pages.flatMap((page) => page.data) ?? [];
  const open = status.data?.open_divergences ?? 0;

  return (
    <>
      <PageHeader
        title="Conciliação"
        sub="Cobranças, saques e saldo contra a posição do parceiro"
        path="/admin/v1/reconciliation/status"
        size="lg"
        actions={
          <Btn
            kind="card"
            icon="lock"
            onClick={() => {
              start.reset();
              setRunOpen(true);
            }}
          >
            Executar agora
          </Btn>
        }
      />

      <StatusPanel
        status={status.data}
        paused={paused.data}
        isLoading={status.isPending}
        error={status.error}
        onRetry={() => {
          void status.refetch();
        }}
      />

      <RunningNotice runs={status.data?.running ?? []} onOpen={openRun} />

      <Panel
        title="Divergências abertas"
        sub="Ainda sem seller destravado desde a execução que encontrou"
        right={
          <Pill tone={open > 0 ? 'bad' : 'ok'}>
            {open} {open === 1 ? 'aberta' : 'abertas'}
          </Pill>
        }
      >
        <OpenDivergences
          rows={divergenceRows}
          isLoading={divergences.isPending}
          error={divergences.error}
          onRetry={() => {
            void divergences.refetch();
          }}
          hasMore={divergences.hasNextPage}
          isLoadingMore={divergences.isFetchingNextPage}
          onLoadMore={() => {
            void divergences.fetchNextPage();
          }}
          onUnlock={(row) => {
            unlock.reset();
            setUnlocking(row);
          }}
        />
      </Panel>

      <Panel
        title="Execuções"
        sub="Cron a cada 15/10 min por tipo, ou disparo manual"
        right={<Mono size={11}>{runRows.length} no histórico</Mono>}
      >
        <RunsList
          rows={runRows}
          isLoading={runs.isPending}
          error={runs.error}
          onRetry={() => {
            void runs.refetch();
          }}
          hasMore={runs.hasNextPage}
          isLoadingMore={runs.isFetchingNextPage}
          onLoadMore={() => {
            void runs.fetchNextPage();
          }}
          onSelect={(run) => {
            openRun(run.id);
          }}
          selectedId={detail?.params.id}
        />
      </Panel>

      <RouteDrawer
        open={detail !== undefined}
        onClose={closeRun}
        title={header.title}
        {...(header.sub === undefined ? {} : { sub: header.sub })}
        width={DRAWER_WIDTH}
      >
        <Outlet />
      </RouteDrawer>

      <RunDialog
        open={runOpen}
        onOpenChange={setRunOpen}
        running={status.data?.running ?? []}
        submitting={start.isPending}
        error={start.error}
        onStart={(body) => {
          start.mutate(body);
        }}
      />

      {/*
        `key` por divergência: no sucesso o dialog fecha por `setUnlocking(undefined)`, sem
        passar pelo `close()` que zera o motivo, sem remontar, a próxima divergência
        abriria com o motivo da anterior já digitado.
      */}
      <UnlockDialog
        key={unlocking === undefined ? 'none' : divergenceKey(unlocking)}
        row={unlocking}
        submitting={unlock.isPending}
        error={unlock.error}
        onClose={() => {
          setUnlocking(undefined);
        }}
        onConfirm={(sellerId, reason) => {
          unlock.mutate({ sellerId, reason });
        }}
      />
    </>
  );
}

export const Route = createFileRoute('/admin/reconciliation')({ component: Reconciliation });
